import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { selectTopMarketsWithOutcomes, daysLeft, marketDisplayName, eventDisplayName } from '../utils';
import OutrightOddButton from './OutrightOddButton';

function TournamentCard({
  event,
  marketCount = Infinity,
  fillHeight = false,
  hideMoreOutcomes = false
}) {
  const { t } = useTranslation('translations', { useSuspense: false });
  const navigate = useNavigate();

  if (!event?.se) return null;
  const se = event.se;
  const eventId = se.id;
  const displayName = eventDisplayName(event, t);
  const top = selectTopMarketsWithOutcomes(event, marketCount, 2);
  const dl = daysLeft(se.schedule_StartTime);
  const dlIsClose = dl != null && dl >= 0 && dl < 30;
  const dlLabel =
    dl == null
      ? ''
      : dl < 0
        ? t('outrights.inProgress')
        : t('outrights.daysLeft', { count: dl });

  const goToMarket = (marketStringId) =>
    navigate(
      `/outrights/${encodeURIComponent(eventId)}?market=${encodeURIComponent(marketStringId || 'all')}`
    );

  if (top.length === 0) return null;

  return (
    <>
      {top.map(({ market, outcomes, totalOutcomes }) => (
        <div
          key={market.stringId || market.id}
          className={`rounded mb-2 match-card-mobile ${fillHeight ? 'd-flex flex-column h-100' : ''}`}
          style={{
            background: 'var(--bs-body-bg)',
            overflow: 'hidden',
            cursor: 'pointer',
            ...(fillHeight ? { minHeight: 113 } : {})
          }}
          onClick={() => goToMarket(market.stringId)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              goToMarket(market.stringId);
            }
          }}
        >
          {/* Compact top band — event name + countdown */}
          <div
            className="px-2 py-1 d-flex align-items-center gap-2"
            style={{
              background:
                'linear-gradient(135deg, var(--bs-primary) 0%, #fff 100%)',
              borderBottom: '1px solid var(--bs-border-color)'
            }}
          >
            <span
              style={{
                fontSize: '0.78rem',
                opacity: 0.95,
                color: '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.2,
                minWidth: 0
              }}
            >
              {displayName}
            </span>
            <span className="flex-grow-1" />
            <span
              className="fw-bold"
              style={{
                fontSize: '0.72rem',
                letterSpacing: '0.06em',
                color: dlIsClose ? 'var(--bs-primary)' : 'var(--bs-body-color)',
                opacity: dlIsClose ? 1 : 0.6,
                whiteSpace: 'nowrap'
              }}
            >
              {dlLabel}
            </span>
          </div>

          {/* Market name + 2 outcomes */}
          <div className={`py-2 px-2 ${fillHeight ? 'd-flex flex-column flex-grow-1' : ''}`}>
            <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
              <div
                className="fs-7 fw-bold text-start"
                style={{
                  color: 'var(--bs-body-color)',
                  opacity: 0.9,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0
                }}
              >
                {marketDisplayName(market, t)}
              </div>
              {!hideMoreOutcomes && totalOutcomes > outcomes.length && (
                <div
                  className="text-end fw-bold"
                  style={{
                    fontSize: '0.7rem',
                    color: 'var(--bs-primary)',
                    opacity: 0.95,
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  {t('outrights.moreOutcomes', { count: totalOutcomes })}
                </div>
              )}
            </div>
            <div
              className={`d-grid ${fillHeight ? 'mt-auto' : ''}`}
              style={{
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                columnGap: '0.25rem'
              }}
            >
              {outcomes.map((outcome) => (
                <OutrightOddButton
                  key={outcome.id}
                  event={event}
                  market={market}
                  outcome={outcome}
                  name={outcome.name}
                />
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
}

export default TournamentCard;
